/**
 * PR 6a — Phase 1.5 — API client boundary for proposal responses.
 *
 * Bridges wire-format payloads from the OMS Go backend (via api-gw) into the
 * frontend-canonical `ProposalResponse` discriminated union. The role
 * discriminator is JWT-derived from the authenticated `Profile`, not taken
 * from the wire payload.
 *
 * Defence-in-depth: `stripAdminFields` removes `cost_cents` / `margin_pct`
 * for comercial responses even when the backend leaks them. See oms#91
 * (`GetLatestRevision`) for the active leak this guards against.
 *
 * Wire role conventions:
 *   - OMS Go middleware emits "ADMIN" | "COMMERCIAL" via `X-User-Scopes`.
 *   - Spring services emit "ROLE_ADMIN" | "ROLE_COMMERCIAL".
 *   - Anything else (RESELLER, empty, unknown) is conservatively mapped to
 *     "comercial" — never escalate privilege from an unrecognised value.
 *
 * See ADR PR 6a, decision A1 and Convenções §3.
 */

import type {
  AdminItemDTO,
  AdminRevisionDTO,
  ComercialItemDTO,
  ComercialRevisionDTO,
  ProposalResponse,
  ProposalRole,
} from "../model/Proposal";

/** Minimal Profile shape this module depends on. The ambient global `Profile`
 *  declared in `src/model/Profile.tsx` is a superset; we intentionally use a
 *  structural subset here so this module is decoupled from that global and
 *  trivially testable without a full Profile factory. */
export interface ProfileLike {
  roles?: string[] | null;
}

/** Map a wire-format role string to the frontend-canonical role (pt-PT).
 *  Conservative default for null, undefined, empty string, or any non-ADMIN
 *  token is "comercial". Recognised admin forms: "ADMIN", "ROLE_ADMIN"
 *  (case-insensitive). Everything else (COMMERCIAL, ROLE_COMMERCIAL,
 *  RESELLER, USER, GUEST, unknown strings) → "comercial". Fail-closed. */
export function translateWireRole(
  wire: string | null | undefined,
): ProposalRole {
  if (!wire) return "comercial";
  const normalised = wire.toUpperCase().replace(/^ROLE_/, "");
  if (normalised === "ADMIN") return "admin";
  // Everything else (COMMERCIAL, RESELLER, USER, GUEST, unknown) → comercial.
  return "comercial";
}

/** Derive the canonical proposal role from the authenticated Profile.
 *  Returns "comercial" when the profile is null/undefined or roles are empty —
 *  never escalate to admin by accident. "ADMIN" or "ROLE_ADMIN" in the roles
 *  array wins over any other value. */
export function deriveProposalRole(
  profile: ProfileLike | null | undefined,
): ProposalRole {
  if (!profile) return "comercial";
  const roles = profile.roles ?? [];
  for (const r of roles) {
    if (translateWireRole(r) === "admin") return "admin";
  }
  return "comercial";
}

/** Explicit allowlist of fields visible to comercial role. Sourced from
 *  `BaseItemDTO` in `model/Proposal.ts`. Any new field added to the backend
 *  payload that is NOT on this list will be silently dropped — this is the
 *  intended fail-closed behaviour. When adding a new comercial-safe field,
 *  extend BOTH `BaseItemDTO` and this list. */
const COMERCIAL_ITEM_KEYS = [
  "product_id",
  "name",
  "reference",
  "component_type",
  "component_type_key",
  "rev3_cents",
  "rev4_cents",
  "pvp_cents",
  "unit_price_cents",
  "min_price_cents",
  "margin_at_risk",
  "quantity",
  "vat_rate_pct",
  "vat_regime",
] as const satisfies ReadonlyArray<keyof ComercialItemDTO>;

/** Bijection enforcement: tsc errors here if a new field is added to
 *  `BaseItemDTO`/`ComercialItemDTO` but the author forgets to add it to
 *  `COMERCIAL_ITEM_KEYS` above. The fail-closed runtime behaviour still
 *  protects us — this just surfaces the omission at PR time so we have
 *  the human conversation (benign field like `currency_code` would
 *  otherwise disappear silently). */
type _AssertAllowlistComplete =
  Exclude<
    keyof ComercialItemDTO,
    (typeof COMERCIAL_ITEM_KEYS)[number]
  > extends never
    ? true
    : never;
const _allowlistCompleteness: _AssertAllowlistComplete = true;
void _allowlistCompleteness;

/** Strip admin-only fields from an item array, returning a new array of
 *  comercial-safe items. Uses an explicit allowlist (not a denylist) so any
 *  new admin-only field added to the backend is dropped by default rather
 *  than leaking until someone notices. Defence-in-depth against backend
 *  leaks (oms#91).
 *
 *  Note: shallow copy. All `BaseItemDTO` fields are primitives today; if a
 *  future revision adds a nested object/array (e.g. `tags: string[]`),
 *  switch to `structuredClone(value)` to avoid shared-reference hazards
 *  between admin source and comercial output.
 *
 *  Field projection semantics: keys whose value is `undefined` are dropped
 *  from the output entirely (the resulting object is allowlist ∩ defined
 *  keys, not just allowlist). `null` is preserved as `null`. Today's OMS
 *  backend never emits explicit `null` for optional numeric fields like
 *  `rev3_cents`, so this convention is observationally identical to a
 *  pure projection — but if backend behaviour ever changes, downstream
 *  consumers must treat `key in item` as the truth, not `item[key] != null`. */
export function stripAdminFields(items: AdminItemDTO[]): ComercialItemDTO[] {
  return items.map((item) => {
    const safe = {} as Record<string, unknown>;
    for (const key of COMERCIAL_ITEM_KEYS) {
      if (key in item) {
        const value = (item as unknown as Record<string, unknown>)[key];
        if (value !== undefined) {
          safe[key] = value;
        }
      }
    }
    return safe as unknown as ComercialItemDTO;
  });
}

/** Build a discriminated `ProposalResponse` from a wire-format admin revision
 *  payload. The role is JWT-derived via `deriveProposalRole(profile)`. When
 *  the resolved role is "comercial", admin fields are stripped from every
 *  item. */
export function buildProposalResponse(
  profile: ProfileLike | null | undefined,
  adminRevision: AdminRevisionDTO,
): ProposalResponse {
  const role = deriveProposalRole(profile);
  if (role === "admin") {
    return { role: "admin", revision: adminRevision };
  }
  const comercialRevision: ComercialRevisionDTO = {
    version: adminRevision.version,
    reference_type: adminRevision.reference_type,
    total_cents: adminRevision.total_cents,
    // Metadata fields are non-sensitive (authorship + workflow state) and
    // visible to both roles. Forwarded as-is; missing values stay missing.
    notes: adminRevision.notes,
    created_by: adminRevision.created_by,
    created_at: adminRevision.created_at,
    change_request_justification: adminRevision.change_request_justification,
    preview_url: adminRevision.preview_url,
    items: stripAdminFields(adminRevision.items),
    // KPI subset without the admin-only margin field.
    kpis: adminRevision.kpis
      ? {
          above_rev3_pct: adminRevision.kpis.above_rev3_pct,
          above_min_pct: adminRevision.kpis.above_min_pct,
        }
      : undefined,
  };
  return { role: "comercial", revision: comercialRevision };
}
